"use client";

import { BreadCrumb, ModalSuccessContact } from "@/components/templates";
import styles from "./styles.module.scss";
import { MyButton } from "@/components/atoms";
import { ChangeEvent, FC, FocusEvent, useState } from "react";
import classNames from "classnames";
import { Formik, FormikHelpers } from "formik";
import * as Yup from "yup";
import axios from "axios";
import Image, { StaticImageData } from "next/image";
import contactImg from "../../../assets/images/contact_img.png";

export interface MyFile {
  extension: string;
  base64: string;
  name: string;
  type: string;
}

export interface TextFieldProps {
  name?: string;
  type?: string;
  value?: any;
  error?: string | false;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  label?: string;
  required?: boolean;
  className?: string;
}

export const TextField: FC<TextFieldProps> = ({
  label,
  name,
  value,
  type = "text",
  error,
  onChange,
  disabled,
  required,
  className,
  onBlur,
}) => {
  return (
    <div className={classNames(styles.input_container, className)}>
      <label className="text-[#999] font-semibold text-xxs md:text-xs font-poppins lg:text-sm">
        {label}
      </label>
      <input
        onBlur={onBlur}
        type={type}
        value={value}
        onChange={onChange}
        disabled={disabled}
        name={name}
        required={required}
      />
      {error && <span className={styles.error_input}>{error}</span>}
    </div>
  );
};

const FormContactObjectValidation = Yup.object({
  nom: Yup.string().required("Champ obligatoire."),
  email: Yup.string()
    .email("Invalide adresse e-mail.")
    .required("Champ obligatoire"),
  prenom: Yup.string().required("Champ obligatoire."),
  tel: Yup.string().required("Champ obligatoire."),
  texte: Yup.string().required("Champ obligatoire."),
  address: Yup.string().required("Champ obligatoire."),
});

interface FormValue {
  nom: string;
  prenom: string;
  tel: string;
  email: string;
  address: string;
  texte: string;
  codePostal: string;
  ville: string;
  file: {
    extension: string;
    base64: string;
    name: string;
    type: string;
  } | null;
  source: string;
}

interface ContactFormProps {
  inContactPage: boolean;
  className?: string;
  homePageClassName?: {
    container: string;
    form: string;
    image: string;
  };
  rightImage?: StaticImageData;
}

export const ContactForm: FC<ContactFormProps> = ({
  className,
  homePageClassName,
  rightImage,
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);

  const handleSubmit = async (
    values: FormValue,
    formHelper: FormikHelpers<FormValue>
  ) => {
    setLoading(true);
    const req = await axios.post("/api/send-mail", values);
    if (req.status === 200) {
      formHelper.resetForm();
      setOpenModal(true);
    }
    setLoading(false);
  };

  return (
    <>
      <ModalSuccessContact
        open={openModal}
        onClose={() => setOpenModal(false)}
      />
      <div className={classNames(styles.form_container, className)}>
        <p className={styles.text}>
          <span className="font-medium">
            Partagez vos questions et vos envies avec nous.{" "}
          </span>
          <br />
          Notre équipe s&apos;engage à répondre à vos questions avec attention
          et à vous guider vers la réalisation de votre projet, en toute
          réactivité.
        </p>
        <p className="font-poppins text-[12px] uppercase tracking-[ 0.48px] mt-[24px] md:mt-[32px]">
          Tous les champs de ce formulaire sont obligatoires.
        </p>
        <div className={homePageClassName?.container}>
          <div className={homePageClassName?.form}>
            <Formik
              initialValues={{
                nom: "",
                prenom: "",
                tel: "",
                texte: "",
                email: "",
                address: "",
              }}
              validationSchema={FormContactObjectValidation}
              onSubmit={handleSubmit as any}
            >
              {({
                handleChange,
                handleBlur,
                handleSubmit,
                values,
                isValid,
                errors,
                touched,
                isSubmitting,
              }) => (
                <form onSubmit={handleSubmit} className={styles.form}>
                  <div className={styles.nom_prenom}>
                    <TextField
                      label="Nom"
                      required
                      className={styles.nom}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.nom}
                      name="nom"
                      error={touched.nom && errors.nom}
                    />
                    <TextField
                      label="Prénom"
                      className={styles.prenom}
                      name="prenom"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.prenom}
                      error={touched.prenom && errors.prenom}
                      required
                    />
                  </div>
                  <TextField
                    label="Email"
                    name="email"
                    className={styles.tel_input}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.email}
                    error={touched.email && errors.email}
                    required
                  />
                  <TextField
                    label="Téléphone"
                    name="tel"
                    className={styles.tel_input}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.tel}
                    error={touched.tel && errors.tel}
                    required
                  />
                  <TextField
                    label="Adresse"
                    name="address"
                    className={styles.tel_input}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.address}
                    error={touched.address && errors.address}
                    required
                  />
                  <div className={styles.textarea_container}>
                    <label className="text-[#999] font-semibold text-xxs md:text-xs font-poppins lg:text-sm">
                      Message
                    </label>
                    <textarea
                      className={styles.textarea}
                      name="texte"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.texte}
                      required
                    />
                    {touched.texte && errors.texte && (
                      <span className={styles.error_input}>{errors.texte}</span>
                    )}
                  </div>
                  <div className="flex flex-col-reverse md:flex-col">
                    <div className={styles.policy_text}>
                      <p>
                        En soumettant ce formulaire, j&apos;accepte la{" "}
                        <a href="/#">politique de confidentialité</a> de ce site
                        web.
                      </p>
                    </div>
                    <div className={styles.submit_btn_container}>
                      <MyButton
                        type="submit"
                        disabled={!isValid || loading || isSubmitting}
                        label={loading ? "Envoi en cours..." : "Envoyer"}
                      />
                    </div>
                  </div>
                </form>
              )}
            </Formik>
          </div>
          {rightImage && (
            <div className={homePageClassName?.image}>
              <Image src={rightImage} alt="Contactez nous" />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export const Contact = () => {
  return (
    <div className={styles.contact}>
      <div className={styles.header}>
        <BreadCrumb
          className={styles.bread_crumb}
          data={[
            {
              url: "/",
              label: "Accueil",
            },
            {
              url: "/contact",
              label: "Contact",
            },
          ]}
        />
        <h1>Nous contacter</h1>
        <h2 className="my-h2 md:hidden">Une question ? Un projet ?</h2>
      </div>
      <div className={styles.contents}>
        <div className={styles.form_container_in_contact_page}>
          <h2 className="my-h2 hidden md:block">Une question ? Un projet ?</h2>
          <div className="mt-[24px] lg:mt-[32px]">
            <ContactForm inContactPage />
          </div>
        </div>
        <div className={styles.infos_container}>
          <div className={styles._block}>
            <div className={styles.container}>
              <h2>
                Infos
                <span />
              </h2>
              <p>COCKENPOT PAYSAGE</p>
              <a
                href="https://www.google.com/maps/place/Cockenpot+PAYSAGE/@50.5454978,2.8217412,17z/data=!3m1!4b1!4m6!3m5!1s0x47dd2f6a166a5681:0xc1923a276b671b2a!8m2!3d50.5454978!4d2.8217412!16s%2Fg%2F1td096kb?entry=ttu"
                target="__blank"
                className="hover:underline"
              >
                <p>
                  Route Nationale 41, <br />
                  59480 Illies / La Bassée
                </p>
              </a>
              <a href="tel:03.20.29.06.24" className="hover:underline">
                <p className={styles.tel}>03.20.29.06.24</p>
              </a>
              <Image
                src={contactImg}
                className="hidden md:block mt-[40px]"
                alt="contactez-nous"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
